import type { RowClassParams } from 'ag-grid-community';

import type { LimaSourcesResponseModal } from '../DevicesTable';
import SOURCE_TYPE from '../SourceCreate/SourceCreate.constants';
import {
  type BaseTreeNode,
  CLOUD_SOURCE_TREE_LEVEL,
  type CloudSourceGridContext,
  type CloudSourceTreeDataBuilderOptions,
  type CloudSourceTreeNode,
  type ReconcileCloudSourcesDetailOptions,
  type RowClassConfig
} from './CloudSourcesTable.types';

/**
 * SentinelOne Managed source type IDs.
 * Child sources with these IDs inherit their parent status.
 *
 * @internal
 */
const SENTINELONE_MANAGED_SOURCE_TYPE_IDS = new Set([60, 61]);

export type SentinelOneParentSourceStatus = Pick<
  LimaSourcesResponseModal,
  'status' | 'status_label'
>;

export type SentinelOneParentSourceStatusMap = ReadonlyMap<
  string,
  SentinelOneParentSourceStatus
>;

/**
 * Collects unique SentinelOne managed parent source UIDs that are missing from
 * the current source list.
 *
 * @param sources - Array of cloud sources from API
 * @returns Unique missing parent source UIDs
 */
export const getMissingSentinelOneManagedParentSourceUids = (
  sources: LimaSourcesResponseModal[] | null | undefined
): string[] => {
  if (!sources || sources.length === 0) return [];

  const sourceMap = new Map<string, LimaSourcesResponseModal>();
  sources.forEach((source) => {
    if (source.source_uid) {
      sourceMap.set(source.source_uid, source);
    }
  });

  const missingParentUids = new Set<string>();

  sources.forEach((source) => {
    if (!SENTINELONE_MANAGED_SOURCE_TYPE_IDS.has(source.source_type_id || 0)) {
      return;
    }

    const parentSourceUid = source.parent_source_uid;
    if (parentSourceUid && !sourceMap.has(parentSourceUid)) {
      missingParentUids.add(parentSourceUid);
    }
  });

  return [...missingParentUids];
};

/**
 * Enriches SentinelOne managed child sources with inherited parent status and
 * hides SentinelOne managed parent rows from the UI dataset.
 *
 * This function:
 * 1. Preserves the visible source list shape and ordering
 * 2. Drops SentinelOne managed parent sources so they are not rendered in the UI
 * 3. For SentinelOne managed child sources, inherits the status from the parent source
 * 4. Falls back to fetched parent status when the parent is not present in the current list
 * 5. Preserves all other source types unchanged
 *
 * **Requirements:**
 * - Parent sources must have a parent_source_uid relationship to child sources
 * - Child sources have parent_source_uid referencing the parent's source_uid
 * - If a child source already has its own status, use the parent status for display only
 *
 * @param sources - Array of cloud sources from API
 * @param parentSourceStatusMap - Cached parent status lookups for missing parents
 * @returns Source array with SentinelOne managed parents removed and child statuses enriched
 *
 * @example
 * ```tsx
 * // Input: [{source_uid: 'p1', source_type_id: 60, parent_source_uid: null, status: 'active'},
 * //         {source_uid: 'c1', source_type_id: 60, parent_source_uid: 'p1', status: null}]
 * // Output: [{source_uid: 'c1', source_type_id: 60, parent_source_uid: 'p1', status: 'active', status_label: ...}]
 * ```
 */
export const filterAndEnrichCloudSources = (
  sources: LimaSourcesResponseModal[] | null | undefined,
  parentSourceStatusMap: SentinelOneParentSourceStatusMap = new Map()
): LimaSourcesResponseModal[] => {
  if (!sources || sources.length === 0) return [];

  // Build a map of source_uid -> source for quick parent lookup
  const sourceMap = new Map<string, LimaSourcesResponseModal>();
  sources.forEach((source) => {
    if (source.source_uid) {
      sourceMap.set(source.source_uid, source);
    }
  });

  return sources.flatMap((source) => {
    const isSentinelOneManagedSource = SENTINELONE_MANAGED_SOURCE_TYPE_IDS.has(
      source.source_type_id || 0
    );

    if (isSentinelOneManagedSource && !source.parent_source_uid) {
      return [];
    }

    if (isSentinelOneManagedSource && source.parent_source_uid) {
      const parentSourceUid = source.parent_source_uid;
      const parentSource = sourceMap.get(parentSourceUid);
      const fetchedParent = parentSourceStatusMap.get(parentSourceUid);
      const inheritedParentStatus =
        parentSource?.status || fetchedParent?.status;
      const inheritedParentStatusLabel =
        parentSource?.status_label ?? fetchedParent?.status_label;

      if (inheritedParentStatus) {
        return [
          {
            ...source,
            status: inheritedParentStatus,
            status_label: inheritedParentStatusLabel
          }
        ];
      }
    }

    return [source];
  });
};

/**
 * Builds a source node from a LimaSourcesResponseModal.
 *
 * Creates the top-level source node for the AG Grid tree data structure.
 * For Microsoft sources with data providers, marks the node as having children.
 *
 * @param source - Cloud source data from API
 * @returns CloudSourceTreeNode representing the source
 *
 * @internal
 */
const buildSourceNode = (
  source: LimaSourcesResponseModal
): CloudSourceTreeNode => {
  const hasChildren =
    source.source_type_id === SOURCE_TYPE.MICROSOFT &&
    Array.isArray(source.data_providers) &&
    source.data_providers.length > 0;

  return {
    id: source.source_uid,
    path: [source.source_uid],
    type: 'source',
    name: `${source.display_source_name || source.source_type?.name || ''}`,
    organization: source.org_label,
    ingestMethod: source.ingest_method || '',
    status: source.status || '',
    statusLabel: source.status_label || '',
    lastSeen: source.last_seen_display || '',
    deviceName: source.device_name || '',
    active: source.active,
    categoryFailedTooltip: source.category_failed_tooltip_text || null,
    sourceData: source,
    hasChildren,
    level: CLOUD_SOURCE_TREE_LEVEL.SOURCE,
    allowSourceActivation: source.allowSourceActivation,
    is_selected: source.is_selected
  };
};

/**
 * Builds provider nodes for Microsoft data providers.
 *
 * Creates child nodes for Microsoft sources that have multiple data providers
 * (e.g., SharePoint, OneDrive, Teams). Returns empty array for non-Microsoft sources.
 *
 * @param source - Parent cloud source
 * @param sourcePath - Path array from parent source
 * @param selectedOrganizationId - Currently selected organization ID
 * @returns Array of CloudSourceTreeNodes for each provider
 *
 * @internal
 */
const buildProviderNodes = (
  source: LimaSourcesResponseModal,
  sourcePath: string[],
  selectedOrganizationId: number
): CloudSourceTreeNode[] => {
  if (
    source.source_type_id !== SOURCE_TYPE.MICROSOFT ||
    !source.data_providers
  ) {
    return [];
  }

  return source.data_providers.map((provider: LimaSourcesResponseModal) => {
    const providerPath = [...sourcePath, provider.source_uid];

    return {
      id: provider.source_uid,
      path: providerPath,
      type: 'provider',
      name: provider.display_source_name || provider.source_type?.name || '',
      organization:
        selectedOrganizationId === 0 ? provider.org_label : undefined,
      ingestMethod: provider.ingest_method || '',
      status: provider.status || '',
      statusLabel: provider.status_label || '',
      lastSeen: provider.last_seen_display || '',
      deviceName: provider.device_name || '',
      active: provider.active,
      categoryFailedTooltip: provider.category_failed_tooltip_text || null,
      providerData: provider,
      sourceData: source,
      hasChildren: false,
      level: CLOUD_SOURCE_TREE_LEVEL.PROVIDER,
      allowSourceActivation: source.allowSourceActivation,
      is_selected: provider.is_selected
    };
  });
};

/**
 * Transforms cloud sources into flat tree rows for AG Grid.
 *
 * Converts the hierarchical source/provider structure into a flat array suitable
 * for AG Grid's Tree Data mode. Each source becomes a top-level node, and Microsoft
 * sources with data providers get child nodes.
 *
 * **Input Validation:**
 * - Returns empty array if sources is null, undefined, or empty
 * - Handles missing or malformed source data gracefully
 *
 * **Tree Structure:**
 * ```
 * Source (Level 0)
 *   └─ Provider 1 (Level 1) [Microsoft only]
 *   └─ Provider 2 (Level 1)
 * ```
 *
 * @param sources - Array of cloud source models from API
 * @param options - Configuration options:
 *   - `selectedOrganizationId` - Current org ID for filtering
 *   - `includeMicrosoftProviders` - Whether to include provider child nodes (default: true)
 * @returns Flattened array of tree nodes with path property for AG Grid Tree Data
 *
 * @example
 * ```tsx
 * const treeData = buildCloudSourceTreeRows(sources, {
 *   selectedOrganizationId: 1,
 *   includeMicrosoftProviders: true
 * });
 * <AgGridReact rowData={treeData} treeData={true} />
 * ```
 */
export const buildCloudSourceTreeRows = (
  sources: LimaSourcesResponseModal[] | null | undefined,
  options: CloudSourceTreeDataBuilderOptions
): CloudSourceTreeNode[] => {
  // Guard against null/undefined/empty inputs
  if (!sources || sources.length === 0) return [];

  // Step 1: Filter out SentinelOne managed parent sources and enrich child sources with parent status
  const filteredAndEnrichedSources = filterAndEnrichCloudSources(sources);

  // Step 2: If filtering resulted in no sources, return empty array
  if (filteredAndEnrichedSources.length === 0) return [];

  const { selectedOrganizationId, includeMicrosoftProviders = true } = options;

  return filteredAndEnrichedSources.flatMap((source) => {
    const sourceNode = buildSourceNode(source);

    const providerNodes = includeMicrosoftProviders
      ? buildProviderNodes(source, sourceNode.path, selectedOrganizationId)
      : [];

    return [sourceNode, ...providerNodes];
  });
};

const CLOUD_SOURCE_ROW_CLASS_CONFIG: RowClassConfig = {
  levelClassMap: {
    [CLOUD_SOURCE_TREE_LEVEL.PROVIDER]: 'cloud-source-table-level-1'
  },
  lastChildClass: 'cloud-source-table-row-is-last',
  selectedRowClass: 'ag-grid-cloud-source-table-row-selected',
  detailOpenRowClass: 'ag-grid-cloud-source-table-row-detail-open'
};

/**
 * Generic utility for generating CSS classes for tree table rows.
 *
 * Applies level-based styling, last child styling, and selection state styling
 * based on configuration. Designed to be reusable across different tree tables.
 *
 * @template T - Tree node type extending BaseTreeNode
 * @param params - AG Grid row class params containing node and data
 * @param config - Configuration object defining class mappings:
 *   - `levelClassMap` - Map of tree level to CSS class name
 *   - `lastChildClass` - CSS class for last child in a group
 *   - `selectedRowClass` - CSS class for selected rows
 *   - `detailOpenRowClass` - CSS class for detail pane open
 * @returns Array of CSS class names to apply to the row
 *
 * @example
 * ```tsx
 * const rowClasses = getTreeRowClass(params, {
 *   levelClassMap: { 0: 'level-0', 1: 'level-1' },
 *   lastChildClass: 'last-child',
 *   selectedRowClass: 'selected',
 *   detailOpenRowClass: 'detail-open'
 * });
 * ```
 */
export const getTreeRowClass = <T extends BaseTreeNode>(
  params: RowClassParams<T>,
  config: RowClassConfig
): string[] => {
  const classes: string[] = [];
  const { level } = params.node;

  // Add level-specific class if mapping exists
  if (level !== null && level !== undefined && config.levelClassMap[level]) {
    classes.push(config.levelClassMap[level]);
  }

  // Add last child class
  if (params.node.lastChild) {
    classes.push(config.lastChildClass);
  }

  // Add selected class
  if (params.node.isSelected()) {
    classes.push(config.selectedRowClass);
  }

  return classes;
};

/**
 * Gets row CSS classes for cloud sources table.
 *
 * Applies tree-level styling, selection state, and detail pane open state.
 * Uses the generic `getTreeRowClass` utility and adds cloud-source-specific logic
 * for highlighting the row when its detail pane is open.
 *
 * **Applied Classes:**
 * - Level-based: `cloud-source-table-level-1` for providers
 * - Last child: `cloud-source-table-row-is-last`
 * - Selected: `ag-grid-cloud-source-table-row-selected`
 * - Detail open: `ag-grid-cloud-source-table-row-detail-open`
 *
 * @param params - AG Grid row class params containing node, data, and context
 * @returns Array of CSS class names to apply to the row
 *
 * @example
 * ```tsx
 * <AgGridReact getRowClass={getCloudSourcesRowClass} />
 * ```
 */
export const getCloudSourcesRowClass = (
  params: RowClassParams<CloudSourceTreeNode>
): string[] => {
  const classes = getTreeRowClass(params, CLOUD_SOURCE_ROW_CLASS_CONFIG);

  // Add detail open class if this row's detail pane is open
  const context = params.context as CloudSourceGridContext;
  const selectedSourceUid = context?.selectedSourceUid;

  if (selectedSourceUid && params.data) {
    // Check if either the source or provider matches the selected source uid
    const isDetailOpen =
      params.data.id === selectedSourceUid ||
      params.data.sourceData?.source_uid === selectedSourceUid ||
      params.data.providerData?.source_uid === selectedSourceUid;

    if (isDetailOpen && CLOUD_SOURCE_ROW_CLASS_CONFIG.detailOpenRowClass) {
      classes.push(CLOUD_SOURCE_ROW_CLASS_CONFIG.detailOpenRowClass);
    }
  }

  return classes;
};

/**
 * Reconciles URL-driven Cloud Sources detail state with the grid as visual-only.
 *
 * This intentionally does not mutate AG Grid checkbox selection because checkbox
 * state drives bulk actions and must only change on explicit user selection.
 */
export const reconcileCloudSourcesDetailHighlight = ({
  api,
  selectedRowId,
  filteredRowIds,
  onMissingSelection
}: ReconcileCloudSourcesDetailOptions) => {
  if (!selectedRowId) {
    api.redrawRows();
    return;
  }

  const node = api.getRowNode(selectedRowId);
  if (node) {
    if (node.rowIndex != null) {
      const pageSize = api.paginationGetPageSize();
      api.paginationGoToPage(Math.floor(node.rowIndex / pageSize));
    }

    api.ensureNodeVisible(node, 'middle');
    api.redrawRows();
    return;
  }

  if (!filteredRowIds.has(selectedRowId)) {
    api.redrawRows();
    onMissingSelection?.();
  }
};
